/**
 * The chosen downsizing plan, as the plan page and Home read it (downsizing rebuild, 21 Oct 2026 — the
 * village pattern, rvChosenPlan.ts).
 *
 * ONE PLAN PER HOME. A family may model more than one home ("Model another home", the mock-up's journey 2):
 * each keeps one saved plan, keyed by the home's name, its price and whether it is bought or rented, and one
 * of them is the chosen plan. Choosing another plan for the same home replaces that home's plan; choosing for
 * another home keeps the first one saved and marks the new one chosen.
 *
 * The card the family chose is stored on the record — its figures, staying put's at the time, the plan in
 * words, the sale and the new home — so the plan page shows exactly what was chosen, with no re-run between.
 */
import { withChosenPlan, type PlanRecordLike } from '../../strategy/chosenPlan.ts';
import type { RiskKind } from '../../strategy/strategyRisks.ts';

export interface DzFigures { legacy: number; pension: number; cashflow: number }

/** The sale and the new home, as the card showed them (dzSaleFigures.ts, the module's one arithmetic). */
export interface DzMoveRecord {
    salePrice: number;
    sellingCosts: number;
    /** 0 when renting. */
    newHomePrice: number;
    stampDuty: number;
    /** The state the duty was struck in; '' when none is known (no duty charged). */
    state: string;
    sellAndRent: boolean;
    rentPerWeek: number;
    /** What the sale leaves once the new home and its duty are paid. */
    freedCapital: number;
    /** What the new home and its duty cost above the sale, from savings. */
    buyUpShortfall: number;
    /** The home's name where one was given ("Elm Street townhouse"); '' otherwise. */
    homeName: string;
    /** When the sale happens (YYYY-MM-DD); '' = now. */
    date: string;
}

export interface DzFamilyRecord {
    personName: string;
    amount: number;
    kind: 'fullBack' | 'moneyBack' | 'incomeOnly';
    incomeAYear: number | null;
    compareNote: string | null;
    rows: Array<{ label: string; fromAnnuity: number; withPlan: number | null; without: number | null }>;
}

export interface DzPlanCardRecord {
    plan: DzFigures;
    baseline: DzFigures | null;
    words: { title: string; sub: string };
    move: DzMoveRecord;
    horizonYears: number;
    riskKinds: RiskKind[];
    /** 'engine' — one of the tested plans; 'manual' — built by hand. */
    origin: 'engine' | 'manual';
    /** A plan with a guaranteed income: what the family receives if death comes early, frozen when chosen. */
    family?: DzFamilyRecord | null;
}

export interface ChosenDzPlan extends PlanRecordLike {
    id: string;
    createdAt?: string;
    soaSnapshot?: unknown;
    source?: string;
    inputs?: { card?: DzPlanCardRecord; homeKey?: string; engineStrategyId?: string | null; details?: Record<string, unknown>; [key: string]: unknown } | null;
}

/**
 * "elm street townhouse|2400000|buy" — the home a plan belongs to, as step 1 put it: the home's name, the price
 * the client named (0 = "not sure — test a range") and buy, rent or compare. Every plan tested for that home
 * shares the key, so choosing another of them replaces the first; another home keeps its own.
 */
export function dzHomeKey(name: string | null | undefined, namedPrice: number, housing: string): string {
    const h = housing === 'rent' || housing === 'both' ? housing : 'buy';
    return `${String(name || '').trim().toLowerCase()}|${h === 'rent' ? 0 : Math.round(Number(namedPrice) || 0)}|${h}`;
}

/** Choosing a plan: the home's earlier plan is replaced, the other homes' plans are kept, the new one is chosen. */
export function withChosenDzPlan<T extends PlanRecordLike>(prev: readonly T[] | null | undefined, record: T): T[] {
    const key = (record.inputs as { homeKey?: string } | null | undefined)?.homeKey;
    const others = (prev || []).filter((p) => !(p.type === 'downsizer' && key != null && (p.inputs as { homeKey?: string } | null | undefined)?.homeKey === key));
    return withChosenPlan(others, record);
}

/** The chosen downsizing plan on the file, if one is chosen and carries its card. */
export function chosenDzPlanOf(list: ReadonlyArray<Record<string, unknown>> | null | undefined): ChosenDzPlan | null {
    const hit = (list || []).find((s) => s?.type === 'downsizer' && s.chosen === true && (s.inputs as { card?: unknown } | undefined)?.card);
    return (hit as ChosenDzPlan | undefined) ?? null;
}

/** Every home's saved plan that carries its card, the chosen one first. */
export function dzHomePlansOf(list: ReadonlyArray<Record<string, unknown>> | null | undefined): ChosenDzPlan[] {
    const withCard = (list || []).filter((s) => s?.type === 'downsizer' && (s.inputs as { card?: unknown } | undefined)?.card) as ChosenDzPlan[];
    return [...withCard].sort((a, b) => Number(b.chosen === true) - Number(a.chosen === true));
}

/** Session key: Home's "Start a new model" — open on step 1 with the questions asked again (one-shot). */
export const DZ_START_FRESH_KEY = 'careCompanionDzStartFresh';
/** Session key: "Model another home" — open the another-home step with everything carried over (one-shot). */
export const DZ_ANOTHER_HOME_KEY = 'careCompanionDzAnotherHome';

/** "$2.73 million", "$850,000" — a price as the cards say it. */
export function dzPriceWords(price: number): string {
    const p = Math.max(0, Math.round(Number(price) || 0));
    if (p >= 1_000_000) {
        const m = Math.round(p / 10_000) / 100;
        return `$${String(m.toFixed(2)).replace(/\.?0+$/, '')} million`;
    }
    return `$${p.toLocaleString('en-AU')}`;
}
